import React from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DSDialog from "../design-system/DSDialog";
import { Icon24Upload } from "../../assets/SVGs/SvgIcons";
import ConversionSteps, { type ConversionStep } from "./ConversionSteps";
import Icon from "../Icon";
import designTokens, {
  colors,
  numbers,
  typography,
} from "../../styles/designTokens";
import type { ObjectImportUiStage } from "../../utils/objectImportProgress";

interface ConversionProgressModalProps {
  open: boolean;
  fileName?: string | null;
  currentStage: ObjectImportUiStage | null;
  stageStartedAt?: number | null;
  onClose: () => void;
}

/**
 * Figma `2886:144777`의 사용자 고정 5단계. backend의 cleanup status는
 * Optimizing 슬롯으로 번역하며 사용자 진행 단계로 노출하지 않는다.
 */
const STAGES = [
  { key: "uploading", label: "Uploading" },
  { key: "converting", label: "Converting" },
  { key: "optimizing", label: "Optimizing" },
  { key: "rendering", label: "Rendering" },
  { key: "completed", label: "Complete" },
] as const;

export const CONVERSION_PROGRESS_TITLE = "Import in progress";

export const CONVERSION_PROGRESS_BACKGROUND_NOTE =
  "Usually a few minutes. This keeps running in the background. You can close this and check the results later in Library or in your project.";

const dialogTokens = designTokens.semantic.dialog;
const bodyStyle = typography.styles.viewrStudio.headline1Regular;

const formatElapsed = (elapsedMs: number): string => {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  if (totalSeconds < 60) return `${totalSeconds} sec elapsed`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes} min ${seconds.toString().padStart(2, "0")} sec elapsed`;
};

const ConversionProgressModal: React.FC<ConversionProgressModalProps> = ({
  open,
  fileName,
  currentStage,
  stageStartedAt,
  onClose,
}) => {
  const [clock, setClock] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!open) return undefined;
    setClock(Date.now());
    const intervalId = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [open, stageStartedAt]);

  const activeIndex = Math.max(
    0,
    STAGES.findIndex((stage) => stage.key === currentStage),
  );
  const elapsedText = formatElapsed(
    typeof stageStartedAt === "number" ? clock - stageStartedAt : 0,
  );

  const steps: ConversionStep[] = STAGES.map((stage, index) => {
    const isComplete = index < activeIndex || currentStage === "completed";
    const isActive = index === activeIndex && currentStage !== "completed";
    return {
      key: stage.key,
      label: stage.label,
      state: isComplete ? "complete" : isActive ? "active" : "pending",
      trailing: isActive ? (
        <Typography
          data-testid="conversion-progress-elapsed"
          sx={{
            ...typography.styles.viewrStudio.captionRegular,
            color: colors.grey[70].dark,
            whiteSpace: "nowrap",
          }}
        >
          {elapsedText}
        </Typography>
      ) : undefined,
    };
  });

  const bodyTextSx = {
    ...bodyStyle,
    color: dialogTokens.typography.bodyColor,
    overflowWrap: "anywhere" as const,
  };

  return (
    <DSDialog
      open={open}
      onClose={onClose}
      size="md"
      aria-labelledby="conversion-progress-title"
    >
      <Box sx={{ height: dialogTokens.layout.topSpacer, width: "100%" }} />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: dialogTokens.layout.headerHeight,
          px: dialogTokens.layout.horizontalPadding,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: dialogTokens.layout.headerGap,
            minWidth: 0,
          }}
        >
          <Box
            aria-hidden="true"
            data-testid="conversion-progress-header-icon"
            sx={{
              width: 24,
              height: 24,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon24Upload size={24} color={dialogTokens.closeButton.color} />
          </Box>
          <Typography
            id="conversion-progress-title"
            sx={{
              ...typography.styles.viewrStudio.headingMedium,
              color: dialogTokens.typography.titleColor,
            }}
          >
            {CONVERSION_PROGRESS_TITLE}
          </Typography>
        </Box>
        <IconButton
          aria-label="Close import progress"
          onClick={onClose}
          sx={{
            p: 0,
            flexShrink: 0,
            width: dialogTokens.closeButton.size,
            height: dialogTokens.closeButton.size,
            color: dialogTokens.closeButton.color,
            "&:hover": {
              color: dialogTokens.closeButton.hoverColor,
              backgroundColor: dialogTokens.closeButton.hoverBackground,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: dialogTokens.closeButton.iconSize }} />
        </IconButton>
      </Box>

      <Box
        data-testid="conversion-progress-body"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          px: numbers.gap[32],
          pt: numbers.gap[8],
          pb: numbers.gap[16],
          boxSizing: "border-box",
        }}
      >
        {fileName && (
          <Box
            data-testid="conversion-progress-file"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: numbers.gap[4],
              width: "100%",
              minWidth: 0,
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
                flexShrink: 0,
                color: colors.grey[70].dark,
              }}
            >
              <Icon name="3d-object-16" />
            </Box>
            <Typography
              sx={{
                ...typography.styles.viewrStudio.label1Medium,
                color: colors.common[100].dark,
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {fileName}
            </Typography>
          </Box>
        )}

        <ConversionSteps data-testid="conversion-progress-steps" steps={steps} />

        <Typography sx={{ ...bodyTextSx, mt: numbers.gap[24] }}>
          {CONVERSION_PROGRESS_BACKGROUND_NOTE}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          height: dialogTokens.layout.actionsHeight,
          px: dialogTokens.layout.horizontalPadding,
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            height: 48,
            minWidth: 100,
            px: numbers.gap[12],
            textTransform: "none",
            ...typography.styles.viewrStudio.bodyRegular,
            backgroundColor: designTokens.fill.shape.primary50.dark,
            color: designTokens.fill.text.common100.dark,
            borderRadius: numbers.cornerRadius[8],
            "&:hover": {
              backgroundColor: designTokens.fill.shape.primary50.dark,
            },
          }}
        >
          Close
        </Button>
      </Box>

      <Box sx={{ height: dialogTokens.layout.bottomSpacer, width: "100%" }} />
    </DSDialog>
  );
};

export default ConversionProgressModal;
